import { useMemo, useState } from 'react'
import { useMutation } from '@tanstack/react-query'
import {
  Check,
  Copy,
  FileText,
  KeyRound,
  Link2,
  Loader2,
  RefreshCw,
  ShieldCheck,
  Terminal,
} from 'lucide-react'
import { toast } from 'sonner'
import { Button } from '@/components/ui/button'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Eyebrow } from '@/components/hawk-primitives'
import { apiFetch } from '@/lib/api/client'
import {
  type ClientSetup,
  TOKEN_PLACEHOLDER,
  resolveSnippet,
} from '@/lib/types/setups'

interface MintedToken {
  id: string
  name: string
  token: string
  expires_at: string | null
}

/**
 * Rich connect picker. Drives the right-side Sheet on /packages/$repo and
 * the admin repos page.
 *
 * Flow:
 *  1. Token block - click "Generate" to mint a fresh PAT scoped to this
 *     repo. Hawk never re-shows tokens, so pasting one in by hand would
 *     mean the user already copied it somewhere insecure; the mint button
 *     keeps the secret on the path it was issued for and nowhere else.
 *  2. Repository URL - the bare URL, copyable separately for tools the
 *     canned recipes don't cover.
 *  3. Client pills + a per-client Select that picks one setup at a time.
 *     Project-scope variants float to the top so the team-friendly option
 *     is the default.
 */
export function RepoConnect({
  setups,
  base,
  orgSlug,
  repoName,
}: {
  setups: ClientSetup[] | null | undefined
  /** The repo's full mount URL, e.g. https://hawk.example.com/repo/org/pypi */
  base: string
  orgSlug: string
  repoName: string
}) {
  const list = setups ?? []
  const [activeToken, setActiveToken] = useState<string | null>(null)

  const clients = useMemo(() => {
    const seen = new Set<string>()
    const order: string[] = []
    for (const s of list) {
      if (!seen.has(s.client)) {
        seen.add(s.client)
        order.push(s.client)
      }
    }
    return order
  }, [list])

  const [client, setClient] = useState(clients[0] ?? '')
  const clientSetups = useMemo(() => {
    const filtered = list.filter((s) => s.client === client)
    return [...filtered].sort((a, b) => {
      if (a.scope === b.scope) return 0
      return a.scope === 'project' ? -1 : 1
    })
  }, [list, client])

  const [setupIdx, setSetupIdx] = useState(0)
  const selected = clientSetups[setupIdx] ?? clientSetups[0]

  function pickClient(c: string) {
    setClient(c)
    setSetupIdx(0)
  }

  if (clients.length === 0) {
    return (
      <div className="text-sm text-muted-foreground italic">
        No setup instructions available for this ecosystem yet.
      </div>
    )
  }

  return (
    <div className="space-y-6">
      <TokenBlock
        orgSlug={orgSlug}
        repoName={repoName}
        onTokenChange={setActiveToken}
      />

      <IndexUrlBlock url={base} />

      <div className="space-y-2.5">
        <Eyebrow marker>Choose your client</Eyebrow>
        <div className="grid grid-cols-3 gap-1.5">
          {clients.map((c) => {
            const active = c === client
            return (
              <button
                key={c}
                type="button"
                onClick={() => pickClient(c)}
                className={`font-mono text-[11px] uppercase tracking-[0.18em] h-9 rounded-md border transition-all ${
                  active
                    ? 'border-primary/50 bg-primary/10 text-primary shadow-[inset_0_0_0_1px_rgba(255,77,0,0.15)]'
                    : 'border-border bg-secondary/30 text-muted-foreground hover:text-foreground hover:border-border/80 hover:bg-secondary/60'
                }`}
              >
                {c}
              </button>
            )
          })}
        </div>
      </div>

      <div className="space-y-3">
        <Eyebrow marker>Configuration</Eyebrow>
        <Select
          value={String(setupIdx)}
          onValueChange={(v) => setSetupIdx(Number(v))}
        >
          <SelectTrigger className="h-10 text-xs">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {clientSetups.map((s, i) => (
              <SelectItem key={i} value={String(i)} className="text-xs">
                <span className="inline-flex items-center gap-2">
                  <span>{s.label}</span>
                  <ScopePill scope={s.scope} />
                </span>
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        {selected && (
          <SetupCard
            setup={selected}
            base={base}
            token={activeToken}
          />
        )}
      </div>
    </div>
  )
}

function TokenBlock({
  orgSlug,
  repoName,
  onTokenChange,
}: {
  orgSlug: string
  repoName: string
  onTokenChange: (token: string | null) => void
}) {
  const [minted, setMinted] = useState<MintedToken | null>(null)

  const mintMutation = useMutation({
    mutationFn: async () => {
      const stamp = new Date().toISOString().slice(0, 10)
      const r = await apiFetch(`/api/orgs/${orgSlug}/tokens`, {
        method: 'POST',
        body: JSON.stringify({
          name: `Connect: ${repoName} (${stamp})`,
          scopes: { read: [repoName], write: [] },
        }),
      })
      if (!r.ok) throw new Error(await r.text())
      return (await r.json()) as MintedToken
    },
    onSuccess: (data) => {
      setMinted(data)
      onTokenChange(data.token)
      toast.success('Token generated')
    },
    onError: (e: Error) => toast.error(e.message),
  })

  if (minted) {
    return (
      <div className="rounded-md border border-primary/30 bg-primary/5 p-4 space-y-2">
        <div className="flex items-center justify-between gap-2">
          <div className="flex items-center gap-2">
            <ShieldCheck className="size-3.5 text-primary" strokeWidth={1.5} />
            <Eyebrow className="text-primary">Token active in snippet</Eyebrow>
          </div>
          <Button
            variant="ghost"
            size="xs"
            onClick={() => mintMutation.mutate()}
            disabled={mintMutation.isPending}
            className="h-6 px-2 text-muted-foreground hover:text-foreground font-mono text-[10px] uppercase tracking-[0.16em] gap-1"
          >
            {mintMutation.isPending ? (
              <Loader2 className="size-3 animate-spin" />
            ) : (
              <RefreshCw className="size-3" strokeWidth={1.5} />
            )}
            Regenerate
          </Button>
        </div>
        <p className="text-[11px] text-muted-foreground leading-relaxed">
          <span className="font-mono text-foreground">{minted.name}</span> is
          baked into the snippet below. Hawk never shows the raw value a
          second time - copy the full snippet now, or revoke the token later
          from <span className="font-mono">Settings → Tokens</span>.
        </p>
      </div>
    )
  }

  return (
    <div className="rounded-md border border-border bg-background/40 p-4 space-y-3">
      <div className="flex items-center gap-2">
        <KeyRound className="size-3 text-primary/80" strokeWidth={1.5} />
        <Eyebrow>Personal access token</Eyebrow>
      </div>
      <p className="text-[11px] text-muted-foreground leading-relaxed">
        The Hawk proxy requires a PAT on every install. If you already have
        one locally, leave the highlighted{' '}
        <code className="font-mono text-foreground/80">YOUR_TOKEN</code>{' '}
        placeholder in the snippet and substitute it on your machine. Or have
        Hawk mint a fresh one scoped to this repo and bake it into the
        snippet directly.
      </p>
      <Button
        onClick={() => mintMutation.mutate()}
        disabled={mintMutation.isPending}
        className="w-full h-9 gap-2"
      >
        {mintMutation.isPending ? (
          <>
            <Loader2 className="size-3.5 animate-spin" />
            Generating
          </>
        ) : (
          <>
            <KeyRound className="size-3.5" strokeWidth={1.5} />
            Generate a new token for this repo
          </>
        )}
      </Button>
    </div>
  )
}

function IndexUrlBlock({ url }: { url: string }) {
  return (
    <div className="rounded-md border border-border bg-background/40 p-4 space-y-2">
      <div className="flex items-center gap-2">
        <Link2 className="size-3 text-primary/80" strokeWidth={1.5} />
        <Eyebrow>Repository URL</Eyebrow>
      </div>
      <div className="flex items-center gap-2">
        <code
          className="flex-1 font-mono text-[12px] text-foreground/90 truncate"
          title={url}
        >
          {url}
        </code>
        <InlineCopyButton value={url} label="URL" />
      </div>
    </div>
  )
}

function SetupCard({
  setup,
  base,
  token,
}: {
  setup: ClientSetup
  base: string
  token: string | null
}) {
  const isCli = setup.kind === 'cli'
  const Icon = isCli ? Terminal : FileText

  return (
    <div className="rounded-md border border-border bg-card/60 overflow-hidden">
      <div className="flex items-center gap-2.5 px-4 py-3 border-b border-border/60 bg-background/30">
        <Icon
          className="size-3.5 text-muted-foreground shrink-0"
          strokeWidth={1.5}
        />
        <span className="text-[13px] font-medium truncate flex-1">
          {setup.label}
        </span>
      </div>

      <SnippetStep
        snippet={setup.snippet}
        kind={setup.kind}
        filePath={setup.file_path}
        base={base}
        token={token}
        stepIndex={1}
        totalSteps={setup.auth ? 2 : 1}
      />

      {setup.auth && (
        <SnippetStep
          snippet={setup.auth.snippet}
          kind={setup.auth.kind}
          filePath={setup.auth.file_path}
          base={base}
          token={token}
          stepIndex={2}
          totalSteps={2}
          label={setup.auth.label}
        />
      )}
    </div>
  )
}

function SnippetStep({
  snippet,
  kind,
  filePath,
  base,
  token,
  stepIndex,
  totalSteps,
  label,
}: {
  snippet: string
  kind: 'cli' | 'config_file'
  filePath: string | null
  base: string
  token: string | null
  stepIndex: number
  totalSteps: number
  label?: string
}) {
  const resolved = resolveSnippet(snippet, base, token)
  const isCli = kind === 'cli'

  return (
    <div className="px-4 py-4 border-t border-border/40 first-of-type:border-t-0 space-y-2">
      {(totalSteps > 1 || label) && (
        <div className="flex items-center gap-2 text-[11px] text-muted-foreground">
          {totalSteps > 1 && (
            <span className="font-mono inline-flex items-center justify-center size-4 rounded-sm bg-secondary text-foreground/70 tabular-nums text-[10px]">
              {stepIndex}
            </span>
          )}
          {label && (
            <span className="font-medium text-foreground/80">{label}</span>
          )}
        </div>
      )}

      {kind === 'config_file' && filePath && (
        <div className="flex items-start gap-1.5 font-mono text-[11px] text-muted-foreground">
          <span className="select-none text-muted-foreground/60">file </span>
          <span className="break-all">
            {resolveSnippet(filePath, base, token)}
          </span>
        </div>
      )}

      <SnippetBlock snippet={resolved} isCli={isCli} />
    </div>
  )
}

function SnippetBlock({
  snippet,
  isCli,
}: {
  snippet: string
  isCli: boolean
}) {
  const [copied, setCopied] = useState(false)

  function copy() {
    navigator.clipboard.writeText(snippet).then(
      () => {
        setCopied(true)
        setTimeout(() => setCopied(false), 1400)
        toast.success('Copied to clipboard')
      },
      () => toast.error('Clipboard unavailable'),
    )
  }

  const lines = snippet.split('\n')

  return (
    <div className="relative rounded-sm bg-background/70 border border-border/70">
      <Button
        variant="ghost"
        size="xs"
        onClick={copy}
        className="absolute top-1.5 right-1.5 h-6 px-2 text-muted-foreground hover:text-primary font-mono text-[10px] uppercase tracking-[0.16em] gap-1 z-10"
      >
        {copied ? (
          <>
            <Check className="size-3" />
            Copied
          </>
        ) : (
          <>
            <Copy className="size-3" />
            Copy
          </>
        )}
      </Button>
      <pre className="p-3 pr-16 font-mono text-[12px] leading-relaxed overflow-x-auto">
        {lines.map((line, i) => (
          <div key={i} className="flex">
            <span
              aria-hidden
              className="select-none w-5 shrink-0 text-muted-foreground/30 tabular-nums text-right pr-2"
            >
              {isCli ? (i === 0 ? '$' : '') : i + 1}
            </span>
            <code className="whitespace-pre-wrap break-all text-foreground/90">
              {renderLineWithHighlight(line)}
            </code>
          </div>
        ))}
      </pre>
    </div>
  )
}

/**
 * If the line contains the literal `YOUR_TOKEN` placeholder, wrap it in a
 * styled span so the user sees what they need to replace at a glance.
 */
function renderLineWithHighlight(line: string): React.ReactNode {
  if (!line.includes(TOKEN_PLACEHOLDER)) return line || ' '
  const parts = line.split(TOKEN_PLACEHOLDER)
  const out: React.ReactNode[] = []
  parts.forEach((part, i) => {
    if (i > 0) {
      out.push(
        <span
          key={`tok-${i}`}
          className="rounded-sm bg-amber-500/15 text-amber-400 px-1 py-px ring-1 ring-inset ring-amber-500/30 font-medium"
        >
          {TOKEN_PLACEHOLDER}
        </span>,
      )
    }
    if (part) out.push(<span key={`p-${i}`}>{part}</span>)
  })
  return out
}

function InlineCopyButton({ value, label }: { value: string; label: string }) {
  const [copied, setCopied] = useState(false)
  return (
    <Button
      variant="outline"
      size="xs"
      onClick={() => {
        navigator.clipboard.writeText(value).then(
          () => {
            setCopied(true)
            setTimeout(() => setCopied(false), 1400)
            toast.success(`${label} copied`)
          },
          () => toast.error('Clipboard unavailable'),
        )
      }}
      className="h-7 gap-1.5 font-mono text-[10px] uppercase tracking-[0.16em]"
    >
      {copied ? (
        <>
          <Check className="size-3" />
          Copied
        </>
      ) : (
        <>
          <Copy className="size-3" />
          Copy
        </>
      )}
    </Button>
  )
}

function ScopePill({ scope }: { scope: 'global' | 'project' }) {
  const cls =
    scope === 'global'
      ? 'text-amber-400 ring-amber-400/30 bg-amber-400/10'
      : 'text-primary ring-primary/30 bg-primary/10'
  return (
    <span
      className={`inline-flex items-center font-mono text-[9px] uppercase tracking-[0.18em] rounded-sm ring-1 ring-inset px-1.5 py-0.5 shrink-0 ${cls}`}
    >
      {scope}
    </span>
  )
}
